import { twJoin } from 'tailwind-merge';

import {
  formatPublicationSource,
  getPublicationIdentifiers,
  type PublicationSummaryView,
  type PublicationView,
} from '@/components/publication/publicationView';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { formatAuthors } from '@/util/formatAuthors';

/**
 * A catalog work as a reader scans it: title, where the record came from when
 * that needs saying, authors, source and identifiers, then the publication's
 * summary when one is ready. The chat's reference sheet and a reply draft's
 * sources both draw it, so a work reads the same wherever it is opened.
 *
 * `title` is passed in rather than read from the publication, because each
 * host falls back differently when the catalog has none. A work with no
 * publication record shows the title alone.
 */
export function PublicationContent({
  title,
  provenance,
  publication,
}: {
  title: string;
  provenance?: string;
  publication?: PublicationView | null;
}) {
  return (
    <VStack space="lg">
      <PublicationDetails title={title} provenance={provenance} publication={publication} />
      <PublicationSummary summary={publication?.summary} />
    </VStack>
  );
}

function PublicationDetails({
  title,
  provenance,
  publication,
}: {
  title: string;
  provenance?: string;
  publication?: PublicationView | null;
}) {
  const authors = publication ? formatAuthors(publication.authors, ', et al') : '';

  const source = publication ? formatPublicationSource(publication) : undefined;

  const identifiers = publication ? getPublicationIdentifiers(publication) : [];

  return (
    <VStack space="sm">
      <Text size="lg" className="font-semibold leading-snug text-typography-900">
        {title}
      </Text>
      {provenance ? (
        <Text size="sm" className="italic text-typography-700">
          {provenance}
        </Text>
      ) : null}
      {authors ? (
        <Text size="sm" className="text-typography-700">
          {authors}
        </Text>
      ) : null}
      {source ? (
        <Text size="sm" className="italic text-typography-700">
          {source}
        </Text>
      ) : null}
      {identifiers.length > 0 ? (
        <HStack className="flex-wrap gap-x-4 gap-y-1">
          {identifiers.map((identifier) => (
            <Text key={identifier.label} size="xs" className="text-typography-700">
              <Text size="xs" className="font-semibold text-typography-700">
                {`${identifier.label} `}
              </Text>
              {identifier.value}
            </Text>
          ))}
        </HStack>
      ) : null}
    </VStack>
  );
}

/** The one line the model writes itself, so the only one that carries the AI note. */
const GENERATED_ROW_LABEL = 'Why it matters';

const SUMMARY_TAKEAWAYS = [
  ['whyItMatters', GENERATED_ROW_LABEL],
  ['headlineResult', 'Headline result'],
] as const;

const SUMMARY_TABLE_ROWS = [
  ['design', 'Design'],
  ['population', 'Population'],
  ['intervention', 'Intervention'],
  ['safety', 'Safety'],
  ['authorsConclusion', 'Conclusion'],
  ['limitations', 'Limitations'],
] as const;

type SummaryKey = (typeof SUMMARY_TAKEAWAYS)[number][0] | (typeof SUMMARY_TABLE_ROWS)[number][0];

/**
 * The publication's reader-facing summary. The API sends none while a summary
 * is missing or held for review, and the sheet then draws nothing at all:
 * never a pending state. Any prose field can be empty on a ready summary, so
 * a row with no text is skipped rather than shown as a bare label.
 *
 * Reading order is the point of the layout: the two lines most readers need
 * lead as plain text, then one table of the study's design, who
 * and what, and what it found. The table keeps a fixed label column so the eye
 * can run down the labels while the text column holds the prose.
 */
function PublicationSummary({ summary }: { summary: PublicationSummaryView | null | undefined }) {
  if (!summary) {
    return null;
  }

  const textOf = (key: SummaryKey) => summary[key]?.trim();

  const takeaways = SUMMARY_TAKEAWAYS.map(([key, label]) => ({ label, text: textOf(key) })).filter(
    (row) => row.text
  );

  const tableRows = SUMMARY_TABLE_ROWS.map(([key, label]) => ({ label, text: textOf(key) })).filter(
    (row) => row.text
  );

  if (takeaways.length === 0 && tableRows.length === 0) {
    return null;
  }

  return (
    <VStack space="lg">
      {takeaways.length > 0 ? (
        <VStack space="lg">
          {takeaways.map((row) => (
            <VStack key={row.label} space="xs">
              <Text size="xs" className="font-semibold uppercase tracking-wide text-typography-700">
                {row.label}
              </Text>
              <Text className="text-base leading-snug text-typography-900">{row.text}</Text>
              {row.label === GENERATED_ROW_LABEL ? (
                <Text size="xs" className="text-typography-700">
                  Summarized by Primum AI
                </Text>
              ) : null}
            </VStack>
          ))}
        </VStack>
      ) : null}
      {tableRows.length > 0 ? (
        <VStack className="border-t border-outline-200">
          {tableRows.map((row, rowIndex) => (
            <HStack
              key={row.label}
              space="md"
              className={twJoin(
                'py-2',
                rowIndex < tableRows.length - 1 && 'border-b border-outline-200'
              )}>
              <Text size="sm" className="w-24 shrink-0 font-semibold text-typography-700">
                {row.label}
              </Text>
              <Text size="sm" className="flex-1 text-typography-900">
                {row.text}
              </Text>
            </HStack>
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
}
